import {
  ModalDialogHeader,
  ModalDialogTitle,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialog,
  ModalDialogContent,
} from '@/components/ModalDialog'
import { observer } from 'mobx-react-lite'
import { Button } from '@/components/Button'
import type ConsultationSchedulerViewModel from '../../ConsultationSchedulerViewModel'
import { ButtonGroup } from '@/components/ButtonGroup'
import { ConsolidatedTimeSlotListContainer } from '../ConsolidatedTimeSlotList/ConsolidatedTimeSlotListContainer'

interface ReviewAvailabilityDialogProps {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Called when the dialog is closed.
   */
  onClose: () => void
  /**
   * Called when edit availability is requested.
   */
  onEdit?: () => void
  /**
   * Content to display in the dialog.
   */
  children: React.ReactNode
  /**
   * Test ID attribute for the dialog.
   */
  testId?: string
  /**
   * Test ID attribute for the cancel button.
   */
  cancelTestId?: string
  /**
   * Test ID attribute for the edit availability button.
   */
  editAvailabilityTestId?: string
}

/**
 * Review Availability Dialog
 */
export function ReviewAvailabilityDialog({
  isOpen,
  onClose,
  onEdit,
  children,
  testId,
  cancelTestId,
  editAvailabilityTestId,
}: ReviewAvailabilityDialogProps) {
  return (
    <ModalDialog size="lg" isOpen={isOpen} onClose={onClose} testId={testId}>
      <ModalDialogHeader>
        <ModalDialogTitle>Review your availability</ModalDialogTitle>
        <ModalDialogDescription>
          These are the dates and times you've marked as available. A Pro will
          book a consultation call with you, based on one of these time slots.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>{children}</ModalDialogContent>
      <ModalDialogFooter>
        <ButtonGroup stretch>
          <Button
            variant="outline"
            label="Cancel"
            onPress={onClose}
            testId={cancelTestId}
          />
          {onEdit && (
            <Button
              label="Edit availability"
              onPress={onEdit}
              testId={editAvailabilityTestId}
            />
          )}
        </ButtonGroup>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface ReviewAvailabilityDialogContainerProps {
  vm: ConsultationSchedulerViewModel
}

/**
 * Review Availability Dialog Container
 */
export const ReviewAvailabilityDialogContainer = observer(
  function ReviewAvailabilityDialogContainer({
    vm,
  }: ReviewAvailabilityDialogContainerProps) {
    const testId = 'review-availability-dialog'
    return (
      <ReviewAvailabilityDialog
        isOpen={vm.reviewAvailabilityDialog.showing}
        onClose={() => vm.reviewAvailabilityDialog.close()}
        onEdit={
          vm.isSettingAvailabilityAllowed
            ? () => vm.showSetAvailabilityDialog()
            : undefined
        }
        testId={testId}
        cancelTestId={`${testId}-cancel-button`}
        editAvailabilityTestId={`${testId}-edit-button`}
      >
        <ConsolidatedTimeSlotListContainer
          timeRanges={vm.currentlySavedAvailability}
        />
      </ReviewAvailabilityDialog>
    )
  },
)
